import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Autocomplete,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowBack, Close, FilterList } from '@mui/icons-material';
import { format } from 'date-fns';
import { formatDateOnly, parseLocalDate } from '../utils/dateUtils';
import { packingSlipService } from '../services/packingSlipService';
import { orderService } from '../services/orderService';
import { formatCurrency } from '../components/CurrencyTextField';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { procurementSummaryFieldsRowSx } from '../components/procurement/ProcurementSummaryBar';
import { useResizableColumns } from '../hooks/useResizableColumns';
import usePersistedListFilters from '../hooks/usePersistedListFilters';

const PACKING_LIST_HEADER_FILTERS_INITIAL = {
  packingSlip: '',
  workOrder: '',
  invoice: '',
  customer: '',
  shipDate: '',
  carrier: '',
  tracking: ''
};

const PACKING_LIST_FILTERS_DEFAULTS = {
  headerColFilters: { ...PACKING_LIST_HEADER_FILTERS_INITIAL },
  sortKey: 'packing_slip_number',
  sortDirection: 'desc',
  page: 0,
  listTab: 0,
  workOrderFilter: ''
};

function trimHeaderFilterObj(obj) {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v ?? '').trim()]));
}

function packingSlipColumnFilterQueryParams(d) {
  const out = {};
  if (d.packingSlip) out.filter_packing_slip = d.packingSlip;
  if (d.workOrder) out.filter_work_order = d.workOrder;
  if (d.invoice) out.filter_invoice = d.invoice;
  if (d.customer) out.filter_customer = d.customer;
  if (d.shipDate) out.filter_ship_date = d.shipDate;
  if (d.carrier) out.filter_carrier = d.carrier;
  if (d.tracking) out.filter_tracking = d.tracking;
  return out;
}

const colHeaderStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 0.375,
  width: '100%',
  pr: 0
};

const colHeaderLabelSlotSx = {
  flexShrink: 0,
  width: '100%',
  minHeight: '1.5rem',
  overflow: 'hidden',
  '& .MuiTableSortLabel-root': {
    maxWidth: '100%',
    minWidth: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.25,
    '& .MuiTableSortLabel-icon': { flexShrink: 0 }
  }
};

const headerFilterTextFieldSx = {
  width: '100%',
  flexShrink: 0,
  '& .MuiInputBase-root': { fontSize: '0.7rem', py: 0.1 },
  '& .MuiInputBase-input': { py: 0.35 }
};

const PackingSlips = () => {
  const DEFAULT_COL_WIDTHS = { packingSlip: 140, workOrder: 130, customer: 200, invoice: 130, shipDate: 110, carrier: 130, tracking: 180, actions: 110 };
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const workOrderIdFromUrl = searchParams.get('work_order_id');
  const partIdFromUrl = searchParams.get('part_id');
  const {
    values: listFilters,
    setField: setListFilter,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_packing_slips_list_filters', PACKING_LIST_FILTERS_DEFAULTS);
  const headerColFilters = listFilters.headerColFilters;
  const setHeaderColFilters = useCallback((v) => setListFilter('headerColFilters', v), [setListFilter]);
  const sortKey = listFilters.sortKey;
  const setSortKey = useCallback((v) => setListFilter('sortKey', v), [setListFilter]);
  const sortDirection = listFilters.sortDirection;
  const setSortDirection = useCallback((v) => setListFilter('sortDirection', v), [setListFilter]);
  const page = listFilters.page;
  const setPage = useCallback((v) => setListFilter('page', v), [setListFilter]);
  const listTab = listFilters.listTab;
  const setListTab = useCallback((v) => setListFilter('listTab', v), [setListFilter]);
  const workOrderFilter = listFilters.workOrderFilter;
  const setWorkOrderFilter = useCallback((v) => setListFilter('workOrderFilter', v), [setListFilter]);
  const urlWorkOrderSeededRef = useRef(false);
  useEffect(() => {
    if (urlWorkOrderSeededRef.current) return;
    if (!workOrderIdFromUrl) return;
    urlWorkOrderSeededRef.current = true;
    setListFilter('workOrderFilter', workOrderIdFromUrl);
  }, [workOrderIdFromUrl, setListFilter]);
  const handleClearFilters = useCallback(() => {
    clearListFilters();
    if (workOrderIdFromUrl || partIdFromUrl) {
      const next = new URLSearchParams(searchParams);
      next.delete('work_order_id');
      next.delete('part_id');
      setSearchParams(next, { replace: true });
    }
  }, [clearListFilters, workOrderIdFromUrl, partIdFromUrl, searchParams, setSearchParams]);
  const [slips, setSlips] = useState([]);
  const [slipsTotal, setSlipsTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const raw = parseInt(window.localStorage.getItem('wm_packing_slips_rows_per_page'), 10);
      return [50, 100, 200].includes(raw) ? raw : 50;
    } catch {
      return 50;
    }
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [debouncedHeaderColFilters, setDebouncedHeaderColFilters] = useState(() =>
    trimHeaderFilterObj(headerColFilters || PACKING_LIST_HEADER_FILTERS_INITIAL)
  );
  const slipsFilterKeyRef = useRef(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsTotal, setEmailLogsTotal] = useState(0);
  const [emailLogsPage, setEmailLogsPage] = useState(0);
  const [emailLogsRowsPerPage, setEmailLogsRowsPerPage] = useState(50);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailLogsError, setEmailLogsError] = useState('');
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_packing_slips_col_widths',
    minWidths: { packingSlip: 100, workOrder: 100, customer: 120, invoice: 90, shipDate: 90, carrier: 100, tracking: 120, actions: 90 }
  });

  useEffect(() => {
    const t = setTimeout(() => {
      const next = trimHeaderFilterObj(headerColFilters);
      setDebouncedHeaderColFilters((prev) => {
        const same = Object.keys(PACKING_LIST_HEADER_FILTERS_INITIAL).every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [headerColFilters]);

  const loadSlips = useCallback(async () => {
    const filterKey = [
      workOrderFilter || '',
      partIdFromUrl || '',
      JSON.stringify(debouncedHeaderColFilters)
    ].join('|');
    const filterJustChanged =
      slipsFilterKeyRef.current != null && slipsFilterKeyRef.current !== filterKey;
    slipsFilterKeyRef.current = filterKey;

    const effectivePage = filterJustChanged ? 0 : page;
    if (filterJustChanged && page !== 0) {
      setPage(0);
    }

    try {
      setLoading(true);
      setError('');
      const params = {
        limit: rowsPerPage,
        offset: effectivePage * rowsPerPage,
        ...(workOrderFilter ? { work_order_id: workOrderFilter } : {}),
        ...(partIdFromUrl ? { part_id: partIdFromUrl } : {}),
        ...packingSlipColumnFilterQueryParams(debouncedHeaderColFilters)
      };
      const res = await packingSlipService.getAll(params);
      const list = res.data || [];
      const total = typeof res?.total === 'number' ? res.total : list.length;
      const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
      if (effectivePage > maxPage) {
        setPage(maxPage);
        return;
      }
      setSlipsTotal(total);
      setSlips(list);
      setSelectedSlip(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Load packing slips:', err);
      setError(err.response?.data?.message || 'Failed to load packing slips');
    } finally {
      setLoading(false);
    }
  }, [workOrderFilter, partIdFromUrl, debouncedHeaderColFilters, page, rowsPerPage]);

  const loadOrders = async () => {
    try {
      const res = await orderService.getAll({ limit: 500 });
      const list = res.data || [];
      // Sort by order_date desc (newest first)
      list.sort((a, b) => {
        const da = a.order_date ? (parseLocalDate(a.order_date)?.getTime() ?? 0) : 0;
        const db = b.order_date ? (parseLocalDate(b.order_date)?.getTime() ?? 0) : 0;
        return db - da;
      });
      setOrders(list);
    } catch (err) {
      console.error('Load orders:', err);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    if (listTab === 0) loadSlips();
  }, [loadSlips, listTab]);

  const loadEmailLogs = useCallback(async () => {
    try {
      setEmailLogsLoading(true);
      setEmailLogsError('');
      const res = await packingSlipService.getNotificationLogs({
        limit: emailLogsRowsPerPage,
        offset: emailLogsPage * emailLogsRowsPerPage
      });
      const list = res.data || [];
      const total = typeof res?.total === 'number' ? res.total : list.length;
      setEmailLogs(list);
      setEmailLogsTotal(total);
    } catch (err) {
      console.error('Load packing slip email logs:', err);
      setEmailLogsError(err.response?.data?.message || 'Failed to load notification history');
    } finally {
      setEmailLogsLoading(false);
    }
  }, [emailLogsPage, emailLogsRowsPerPage]);

  useEffect(() => {
    if (listTab === 1) loadEmailLogs();
  }, [listTab, loadEmailLogs]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this packing slip? Invoices are not deleted.')) return;
    try {
      await packingSlipService.delete(id);
      await loadSlips();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  const allOption = { id: '', isAll: true };
  const workOrderOptions = [allOption, ...orders];
  const selectedOrder = workOrderFilter
    ? orders.find((o) => String(o.id) === workOrderFilter) ?? null
    : allOption;
  const getWorkOrderLabel = (o) => {
    if (!o || o.isAll) return 'All';
    const dateStr = o.order_date ? formatDateOnly(o.order_date, 'MM/dd') : '—';
    const customerName = o.company_name || [o.customer_first_name, o.customer_last_name].filter(Boolean).join(' ') || '—';
    const amount = formatCurrency(o.total_amount);
    const woNumber = o.work_order_identifier ?? o.work_order_number ?? o.id;
    return `[#${woNumber}] ${dateStr} ${customerName} ${amount}`;
  };

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedSlips = useMemo(() => {
    const getter = (row) => {
      switch (sortKey) {
        case 'work_order_number': return row.work_order_identifier ?? row.work_order_number ?? '';
        case 'packing_slip_number': return row.identifier ?? row.packing_slip_number ?? '';
        case 'customer': return row.customer_name ?? '';
        case 'shipped_date': return row.shipped_date ? new Date(row.shipped_date).getTime() : 0;
        case 'carrier': return row.carrier ?? '';
        case 'tracking_number': return row.tracking_number ?? '';
        case 'invoice': return row.invoice_identifier ?? row.invoice_number ?? '';
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...slips].sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [slips, sortKey, sortDirection]);

  return (
    <Box>
      <Box className="no-print-shipments">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {workOrderIdFromUrl && (
          <IconButton size="small" onClick={() => navigate(`/work-orders/${workOrderIdFromUrl}`)} title="Back to order">
            <ArrowBack />
          </IconButton>
        )}
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Shipments
        </Typography>
      </Box>

      <Tabs
        value={listTab}
        onChange={(_, v) => setListTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Packing slips" id="shipments-tab-list" aria-controls="shipments-tabpanel-list" />
        <Tab label="Notification history" id="shipments-tab-emails" aria-controls="shipments-tabpanel-emails" />
      </Tabs>

      {listTab === 1 && (
        <Box role="tabpanel" id="shipments-tabpanel-emails" aria-labelledby="shipments-tab-emails">
          {emailLogsError && (
            <Alert severity="error" onClose={() => setEmailLogsError('')} sx={{ mb: 2 }}>
              {emailLogsError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Invoice / shipment notification emails. Rows for deleted packing slips stay in history and are grayed out.
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Sent</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Packing slip</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Subject</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emailLogsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : emailLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No notification emails yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  emailLogs.map((log) => {
                    const deleted = !!log.slip_deleted;
                    const mutedSx = deleted
                      ? { color: 'text.disabled', opacity: 0.72 }
                      : undefined;
                    return (
                      <TableRow
                        key={log.id}
                        hover={!deleted}
                        sx={
                          deleted
                            ? {
                                bgcolor: 'action.hover',
                                '& td': { color: 'text.secondary' }
                              }
                            : undefined
                        }
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap', ...mutedSx }}>
                          {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell sx={mutedSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={log.status || '—'}
                              color={
                                log.status === 'sent'
                                  ? 'success'
                                  : log.status === 'failed'
                                    ? 'error'
                                    : 'default'
                              }
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                            {deleted && (
                              <Chip
                                size="small"
                                label="Slip deleted"
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={mutedSx}>
                          {deleted ? (
                            <span>{log.packing_slip_identifier || '—'}</span>
                          ) : log.packing_slip_id ? (
                            <Button
                              size="small"
                              sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                              onClick={() => navigate(`/packing-slips/${log.packing_slip_id}`)}
                            >
                              {log.packing_slip_identifier || `#${log.packing_slip_id}`}
                            </Button>
                          ) : (
                            log.packing_slip_identifier || '—'
                          )}
                        </TableCell>
                        <TableCell sx={mutedSx}>
                          {log.work_order_id ? (
                            <Button
                              size="small"
                              sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                              onClick={() => navigate(`/work-orders/${log.work_order_id}`)}
                            >
                              {log.work_order_identifier ?? log.work_order_number ?? `#${log.work_order_id}`}
                            </Button>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell sx={mutedSx}>{log.customer_name || '—'}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            ...mutedSx
                          }}
                          title={log.recipient_emails || ''}
                        >
                          {log.recipient_emails || '—'}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 320,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            ...mutedSx
                          }}
                          title={log.subject || ''}
                        >
                          {log.subject || '—'}
                          {log.error ? (
                            <Typography variant="caption" color="error" display="block" noWrap title={log.error}>
                              {log.error}
                            </Typography>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              rowsPerPageOptions={[25, 50, 100]}
              count={emailLogsTotal}
              rowsPerPage={emailLogsRowsPerPage}
              page={emailLogsPage}
              onPageChange={(_, newPage) => setEmailLogsPage(newPage)}
              onRowsPerPageChange={(e) => {
                setEmailLogsRowsPerPage(parseInt(e.target.value, 10));
                setEmailLogsPage(0);
              }}
              labelRowsPerPage="Rows per page"
            />
          </TableContainer>
        </Box>
      )}

      {listTab === 0 && (
      <Box role="tabpanel" id="shipments-tabpanel-list" aria-labelledby="shipments-tab-list">
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Deleted packing slips stay in this list (grayed out with a Deleted chip) so shipment history is visible.
      </Typography>

      {/* Selected Shipment Information Bar */}
      <Paper sx={{ p: 1.5, mb: 2, backgroundColor: 'surfaceMuted.main', border: 1, borderColor: 'borderSubtle.main' }}>
        <Box sx={procurementSummaryFieldsRowSx()}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Work Order</Typography>
            <Typography variant="body2">
              {selectedSlip?.work_order_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/work-orders/${selectedSlip.work_order_id}`)}
                >
                  {selectedSlip?.work_order_identifier ?? selectedSlip?.work_order_number ?? selectedSlip?.work_order_id}
                </Button>
              ) : (
                selectedSlip?.work_order_identifier ?? selectedSlip?.work_order_number ?? selectedSlip?.work_order_id ?? '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Packing Slip #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedSlip?.identifier ?? selectedSlip?.packing_slip_number ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice</Typography>
            <Typography variant="body2">
              {selectedSlip?.invoice_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customer-invoices/${selectedSlip.invoice_id}`)}
                >
                  {selectedSlip?.invoice_identifier ?? selectedSlip?.invoice_number ?? `#${selectedSlip.invoice_id}`}
                </Button>
              ) : (
                '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
            <Typography variant="body2">
              {selectedSlip?.customer_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customers/${selectedSlip.customer_id}`)}
                >
                  {selectedSlip?.customer_name || `#${selectedSlip.customer_id}`}
                </Button>
              ) : (
                selectedSlip?.customer_name ?? '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Ship Date</Typography>
            <Typography variant="body2">
              {formatDateOnly(selectedSlip?.shipped_date) || '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Date Generated</Typography>
            <Typography variant="body2">
              {formatDateOnly(selectedSlip?.created_at) || '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Carrier</Typography>
            <Typography variant="body2">
              {selectedSlip?.carrier ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Tracking</Typography>
            <Typography variant="body2">
              {selectedSlip?.tracking_number ?? '—'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setSelectedSlip(null)}
            disabled={!selectedSlip}
            sx={{ ml: 'auto' }}
            title="Clear selection"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <Paper sx={{ p: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 600 }}
            ListboxProps={{
              sx: {
                '& .MuiAutocomplete-option': {
                  py: 0.5,
                  minHeight: 'auto',
                  color: '#000000',
                },
              },
            }}
            options={workOrderOptions}
            value={selectedOrder}
            onChange={(_, newValue) => setWorkOrderFilter(newValue && !newValue.isAll ? String(newValue.id) : '')}
            getOptionLabel={getWorkOrderLabel}
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  component="span"
                  sx={{
                    color: '#000000 !important',
                    fontSize: '0.875rem',
                    display: 'block',
                    py: 0.25,
                    fontWeight: 500,
                  }}
                >
                  {getWorkOrderLabel(option)}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Work order" placeholder="All or search by date, customer, amount..." />
            )}
            isOptionEqualToValue={(opt, val) => (opt && val && opt.isAll && val.isAll) || (opt && val && opt.id === val.id)}
          />
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => navigate(workOrderFilter ? `/packing-slips/new?work_order_id=${workOrderFilter}` : '/packing-slips/new')}>
            New packing slip
          </Button>
          {(hasActiveFilters || workOrderIdFromUrl || partIdFromUrl) ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              onClick={handleClearFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear filters
            </Button>
          ) : null}
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: { sm: 'auto' } }}>
            {slipsTotal === 0
              ? 'No packing slips match the current filters.'
              : slips.length === 0
                ? `Page ${page + 1} · ${slipsTotal} packing slip${slipsTotal === 1 ? '' : 's'} total`
                : `Showing ${page * rowsPerPage + 1}–${page * rowsPerPage + slips.length} of ${slipsTotal} packing slip${slipsTotal === 1 ? '' : 's'}`}
          </Typography>
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '60vh',
          overflowY: 'auto',
          overflowX: 'auto',
          scrollbarGutter: 'stable'
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            '& .MuiTableCell-head': { verticalAlign: 'top' },
            '& .MuiTableHead-root .MuiTableCell-stickyHeader': {
              top: -13,
              zIndex: 20,
              backgroundColor: 'background.paper',
              backgroundImage: 'none',
              backgroundClip: 'padding-box',
              opacity: 1,
              overflow: 'hidden',
              borderTop: '1px solid',
              borderTopColor: 'background.paper',
              boxShadow: (theme) =>
                `inset 0 -1px 0 rgba(224,224,224,0.9), 0 -13px 0 0 ${theme.palette.background.paper}`
            }
          }}
        >
          <TableHead>
            <TableRow>
              <ResizableHeaderCell width={colWidths.packingSlip} onResizeStart={(e) => startResize('packingSlip', e)}>
                <Box sx={colHeaderStackSx}>
                  <Box sx={colHeaderLabelSlotSx}>
                    <TableSortLabel active={sortKey === 'packing_slip_number'} direction={sortDirection} onClick={() => handleSort('packing_slip_number')}>
                      Packing Slip ID
                    </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.packingSlip}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, packingSlip: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={headerFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.workOrder} onResizeStart={(e) => startResize('workOrder', e)}>
                <Box sx={colHeaderStackSx}>
                  <Box sx={colHeaderLabelSlotSx}>
                    <TableSortLabel active={sortKey === 'work_order_number'} direction={sortDirection} onClick={() => handleSort('work_order_number')}>
                      Work Order ID
                    </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.workOrder}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, workOrder: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={headerFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.invoice} onResizeStart={(e) => startResize('invoice', e)}>
                <Box sx={colHeaderStackSx}>
                  <Box sx={colHeaderLabelSlotSx}>
                    <TableSortLabel active={sortKey === 'invoice'} direction={sortDirection} onClick={() => handleSort('invoice')}>
                      Invoice ID
                    </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.invoice}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, invoice: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={headerFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.customer} onResizeStart={(e) => startResize('customer', e)}>
                <Box sx={colHeaderStackSx}>
                  <Box sx={colHeaderLabelSlotSx}>
                    <TableSortLabel active={sortKey === 'customer'} direction={sortDirection} onClick={() => handleSort('customer')}>
                      Customer
                    </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.customer}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, customer: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={headerFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.shipDate} onResizeStart={(e) => startResize('shipDate', e)}>
                <Box sx={colHeaderStackSx}>
                  <Box sx={colHeaderLabelSlotSx}>
                    <TableSortLabel active={sortKey === 'shipped_date'} direction={sortDirection} onClick={() => handleSort('shipped_date')}>
                      Ship Date
                    </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.shipDate}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, shipDate: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={headerFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.carrier} onResizeStart={(e) => startResize('carrier', e)}>
                <Box sx={colHeaderStackSx}>
                  <Box sx={colHeaderLabelSlotSx}>
                    <TableSortLabel active={sortKey === 'carrier'} direction={sortDirection} onClick={() => handleSort('carrier')}>
                      Carrier
                    </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.carrier}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, carrier: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={headerFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.tracking} onResizeStart={(e) => startResize('tracking', e)}>
                <Box sx={colHeaderStackSx}>
                  <Box sx={colHeaderLabelSlotSx}>
                    <TableSortLabel active={sortKey === 'tracking_number'} direction={sortDirection} onClick={() => handleSort('tracking_number')}>
                      Tracking
                    </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.tracking}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, tracking: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={headerFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)} align="right">
                Actions
              </ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress /></TableCell></TableRow>
            ) : displayedSlips.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">No packing slips</TableCell></TableRow>
            ) : (
              displayedSlips.map((row) => {
                const deleted = !!row.deleted;
                return (
                <TableRow
                  key={deleted ? `deleted-${row.id}` : row.id}
                  hover={!deleted}
                  selected={!deleted && selectedSlip?.id === row.id}
                  onClick={() => {
                    if (!deleted) setSelectedSlip(row);
                  }}
                  onDoubleClick={() => {
                    if (!deleted) navigate(`/packing-slips/${row.id}`);
                  }}
                  sx={{
                    cursor: deleted ? 'default' : 'pointer',
                    ...(deleted
                      ? {
                          bgcolor: 'action.hover',
                          opacity: 0.78,
                          '& td': { color: 'text.secondary' }
                        }
                      : {})
                  }}
                >
                  <TableCell
                    sx={{
                      width: colWidths.packingSlip,
                      '&.MuiTableCell-body:first-of-type': { fontWeight: 400 },
                      ...(deleted ? { textDecoration: 'line-through' } : {})
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <span>{row.identifier ?? row.packing_slip_number ?? `Slip #${row.id}`}</span>
                      {deleted && (
                        <Chip label="Deleted" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                      {!deleted && row.shipment_purpose === 'vendor_outbound' && (
                        <Chip label="Vendor" size="small" color="secondary" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: colWidths.workOrder }} onClick={(e) => e.stopPropagation()}>
                    {row.work_order_id ? (
                      <Button size="small" onClick={() => navigate(`/work-orders/${row.work_order_id}`)}>
                        {row.work_order_identifier ?? row.work_order_number ?? row.work_order_id}
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.invoice }} onClick={(e) => e.stopPropagation()}>
                    {!deleted && row.invoice_id ? (
                      <Button size="small" onClick={() => navigate(`/customer-invoices/${row.invoice_id}`)}>
                        {row.invoice_identifier ?? row.invoice_number ?? `#${row.invoice_id}`}
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.customer }}>{row.customer_name ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.shipDate }}>{formatDateOnly(row.shipped_date) || '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.carrier }}>{row.carrier ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.tracking }}>{row.tracking_number ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.actions }} align="right" onClick={(e) => e.stopPropagation()}>
                    {deleted ? (
                      <Typography variant="caption" color="text.disabled" sx={{ pr: 1 }}>
                        —
                      </Typography>
                    ) : (
                      <>
                        <IconButton size="small" onClick={() => navigate(`/packing-slips/${row.id}`)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id)} title="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          rowsPerPageOptions={[50, 100, 200]}
          count={slipsTotal}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            setRowsPerPage(next);
            setPage(0);
            try {
              window.localStorage.setItem('wm_packing_slips_rows_per_page', String(next));
            } catch {
              // ignore quota / private mode
            }
          }}
          labelRowsPerPage="Rows per page"
        />
      </TableContainer>
      </Box>
      )}
      </Box>

      {/* Print view – shipments list */}
      <PrintPage title="Shipments" showSpacer={false}>
        <p style={{ fontSize: 12, marginBottom: 8 }}>
          {slipsTotal} record(s) matching filters · {slips.length} on this page. Printed {format(new Date(), 'MM/dd/yyyy HH:mm')}
        </p>
        <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: 6, textAlign: 'left' }}>Packing Slip #</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Work Order</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Invoice ID</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Customer</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Ship Date</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Carrier</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Tracking</th>
            </tr>
          </thead>
          <tbody>
            {slips.length > 0 ? (
              slips.map((row) => (
                <tr key={row.id}>
                  <td style={{ padding: 6 }}>{row.identifier ?? row.packing_slip_number ?? '—'}</td>
                  <td style={{ padding: 6 }}>{row.work_order_identifier ?? row.work_order_number ?? row.work_order_id ?? '—'}</td>
                  <td style={{ padding: 6 }}>
                    {row.invoice_id
                      ? (row.invoice_identifier ?? row.invoice_number ?? `#${row.invoice_id}`)
                      : '—'}
                  </td>
                  <td style={{ padding: 6 }}>{row.customer_name ?? '—'}</td>
                  <td style={{ padding: 6 }}>
                    {formatDateOnly(row.shipped_date) || '—'}
                  </td>
                  <td style={{ padding: 6 }}>{row.carrier ?? '—'}</td>
                  <td style={{ padding: 6 }}>{row.tracking_number ?? '—'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ padding: 6, textAlign: 'center' }}>No packing slips</td>
              </tr>
            )}
          </tbody>
        </table>
      </PrintPage>
    </Box>
  );
};

export default PackingSlips;
